// Horizontal supine patient avatar (head LEFT, feet RIGHT, bird's-eye anterior view).
// Body: CC0 3D-shaded male illustration from Wikimedia Commons
//   (Male_front_3d-shaded_human_illustration_lying.svg) — served from /avatars/patient-lying.svg
// Equipment overlays (trolley, gown, ECG, SpO2 probe, NIBP cuff, IV, defib pads, airway, CPR
// hands) drawn as SVG primitives on top, state-driven.
//
// Cyanosis / pallor / ROSC pinking applied as CSS `filter` on the body <image>.
//
// Coordinate system: viewBox 0 0 1100 480.
//   Body image rendered at x=77..1023, y=50..450 (after meet aspect-ratio fit on 1020×400 box).
//   Landmarks (measured from PIL pixel-search on the body image — eye iris darkest-pixel cluster):
//     Skull / hair ........ x  80..130,  y 180..305  (left of face; head points LEFT)
//     Face front .......... x 110..210,  y 215..285
//     Right eye (patient) . x ~140,      y ~234  (top-most eye in image; face rotated 90° CCW)
//     Left  eye (patient) . x ~140,      y ~270
//     Nose ................ x ~165,      y ~252
//     Mouth / lips ........ x ~185,      y ~252
//     Chin ................ x ~210,      y ~250
//     Neck ................ x 230..290,  y 215..305
//     Sternum (CPR) ....... x ~317,      y ~250
//     Right shoulder ...... x ~290,      y ~180  (top of image, patient's right)
//     Left  shoulder ...... x ~290,      y ~325  (bottom of image, patient's left)
//     Right hand (top) .... x ~520..600, y  50..115
//     Left  hand (bottom) . x ~460..560, y 400..450
//     Sternal defib pad ... x ~320,      y ~220  (upper-right chest, below clavicle)
//     Apex defib pad ...... x ~400,      y ~295  (lower-left chest, mid-axillary)

import type { Rhythm } from '../../lib/waveforms';

interface Props {
  rhythm: Rhythm;
  hr: number;
  spo2: number;
  cpr_active: boolean;
  cpr_quality_pct: number;
  airway: string;
  current_phase: string;
  intubated: boolean;
}

const ARREST_RHYTHMS = new Set<Rhythm>(['vf', 'vt_pulseless', 'asystole', 'pea_narrow', 'pea_broad', 'torsades']);

function bodyFilter(spo2: number, rhythm: Rhythm, phase: string): string {
  const arrest = ARREST_RHYTHMS.has(rhythm);
  if (arrest && spo2 < 30) return 'hue-rotate(190deg) saturate(0.45) brightness(0.78) contrast(0.95)';
  if (arrest)              return 'hue-rotate(205deg) saturate(0.55) brightness(0.85) contrast(0.95)';
  if (phase === 'rosc' && spo2 < 92) return 'hue-rotate(-8deg) saturate(0.85) brightness(0.94)';
  if (spo2 < 88)           return 'hue-rotate(-6deg) saturate(0.75) brightness(0.9)';
  return 'none';
}

export default function PatientAvatar({ rhythm, hr, spo2, cpr_active, cpr_quality_pct, airway, current_phase, intubated }: Props) {
  const filter = bodyFilter(spo2, rhythm, current_phase);
  const inArrest = ARREST_RHYTHMS.has(rhythm);
  const eyesClosed = inArrest || current_phase === 'arrest' || (current_phase === 'rosc' && hr === 0);
  const breathing = intubated && !inArrest && hr > 0;

  return (
    <div className="panel relative overflow-hidden p-2 flex items-center justify-center" style={{ minHeight: 260 }}>
      <svg
        viewBox="0 0 1100 480"
        preserveAspectRatio="xMidYMid meet"
        className="w-full max-h-[300px]"
        style={{ height: 'auto' }}
        aria-label="Patient avatar — supine, bird's-eye, head left"
      >
        {/* ============ Trolley + side rails ============ */}
        <rect x="10" y="20" width="1080" height="440" rx="14" fill="#1a212d" />
        <rect x="10" y="20" width="1080" height="440" rx="14" fill="none" stroke="#2c3543" strokeWidth="1" />
        <rect x="180" y="14"  width="700" height="14" rx="3" fill="#2c3543" />
        <rect x="180" y="452" width="700" height="14" rx="3" fill="#2c3543" />

        {/* ============ Pillow under head ============ */}
        <path d="M 30 170
                 Q 30 150 70 150
                 L 200 150
                 Q 220 150 220 170
                 L 220 320
                 Q 220 340 200 340
                 L 70 340
                 Q 30 340 30 320 Z"
              fill="#3a4255" />
        <path d="M 38 178 Q 38 162 70 162 L 200 162 Q 214 162 214 178 L 214 312" fill="none" stroke="#566379" strokeWidth="0.8" />

        {/* ============ Body image (3D-shaded illustration) — CSS-filtered for cyanosis/pallor ============ */}
        <g className={cpr_active ? 'als-cpr-compress' : breathing ? 'als-breathe' : ''} style={{ transformOrigin: '320px 250px' }}>
          <image
            href="/avatars/patient-lying.svg"
            x="40" y="50" width="1020" height="400"
            preserveAspectRatio="xMidYMid meet"
            style={{ filter }}
          />
        </g>

        {/* ============ Hospital gown (covers torso, semi-transparent) ============ */}
        <g className={cpr_active ? 'als-cpr-compress' : breathing ? 'als-breathe' : ''} opacity="0.82" style={{ transformOrigin: '320px 250px' }}>
          <path d="M 200 175
                   Q 195 200 200 240
                   L 210 295
                   Q 220 325 240 340
                   L 420 340
                   Q 450 330 470 305
                   L 480 260
                   L 478 200
                   Q 460 178 420 172
                   L 250 172
                   Q 215 172 200 175 Z"
                fill="#a8d5d8" />
          {/* V-collar */}
          <path d="M 220 180 L 248 240 L 276 180" fill="none" stroke="#5e9da0" strokeWidth="1.6" />
          {/* Sternum dashed reference */}
          <line x1="290" y1="250" x2="430" y2="252" stroke="#5e9da0" strokeWidth="0.8" opacity="0.5" strokeDasharray="3 4" />
          {/* Lower side tie */}
          <path d="M 472 296 Q 492 300 498 312" fill="none" stroke="#5e9da0" strokeWidth="1.4" />
        </g>

        {/* ============ Sheet covering lower body (hips → feet) — extended LEFT to cover the
             pelvis/genital region; before it started at x=610 and exposed everything below
             the gown. Now it picks up just below the gown line at x=440. ============ */}
        <g>
          <path d="M 440 165
                   Q 430 200 432 250
                   Q 434 305 442 355
                   Q 510 372 600 370
                   Q 720 372 840 366
                   Q 960 362 1040 355
                   L 1066 348
                   Q 1078 280 1066 200
                   L 1040 150
                   Q 960 140 840 138
                   Q 720 134 600 134
                   Q 510 132 442 144
                   Q 438 154 440 165 Z"
                fill="#e4e6ea" />
          {/* Sheet folds — running roughly along body axis */}
          <line x1="470" y1="180" x2="1050" y2="195" stroke="#bbbfc6" strokeWidth="0.6" opacity="0.6" />
          <line x1="475" y1="240" x2="1055" y2="240" stroke="#bbbfc6" strokeWidth="0.6" opacity="0.6" />
          <line x1="470" y1="320" x2="1050" y2="295" stroke="#bbbfc6" strokeWidth="0.6" opacity="0.6" />
          {/* Hip drape — the sheet humps over the pelvis */}
          <ellipse cx="475" cy="250" rx="22" ry="65" fill="#d6dadf" opacity="0.55" />
          {/* Foot bumps */}
          <ellipse cx="1010" cy="210" rx="28" ry="18" fill="#c8ccd2" opacity="0.55" />
          <ellipse cx="1010" cy="290" rx="28" ry="18" fill="#c8ccd2" opacity="0.55" />
        </g>

        {/* ============ Face overlays: closed eyelids during arrest ============ */}
        {/* The body image has its own open eyes when alive — we draw lash-line arcs at the actual eye positions during arrest. */}
        {eyesClosed && (
          <g>
            {/* Face is rotated 90° CCW (head pointing LEFT). Eyes sit higher on the face
                than the geometric centre — moved LEFT (toward forehead) so the lashes are
                clear of the cheeks/nose. Vertical ellipse + lash arc that bulges toward
                the forehead. */}
            {/* Patient's right eye (upper in image) — eye at x≈122 y≈232 */}
            <ellipse cx="122" cy="232" rx="3" ry="11" fill="#d6b39c" opacity="0.85" />
            <path d="M 122 222 Q 118 232 122 242" fill="none" stroke="#1c1715" strokeWidth="1.8" strokeLinecap="round" />
            {/* Patient's left eye (lower in image) — eye at x≈122 y≈272 */}
            <ellipse cx="122" cy="272" rx="3" ry="11" fill="#d6b39c" opacity="0.85" />
            <path d="M 122 262 Q 118 272 122 282" fill="none" stroke="#1c1715" strokeWidth="1.8" strokeLinecap="round" />
          </g>
        )}

        {/* ============ Mouth + airway (mouth at x≈185 y≈252) ============ */}
        {/* All airway devices enter at the mouth and the tubing trails LEFT off the head end (where BVM/anaesthetic machine would sit). */}
        {airway === 'opa' && (
          <g>
            <rect x="165" y="249" width="34" height="6" rx="2" fill="#d6d9de" stroke="#777" strokeWidth="0.7" />
            <rect x="157" y="247" width="10" height="10" rx="1" fill="#d6d9de" stroke="#777" strokeWidth="0.6" />
          </g>
        )}
        {airway === 'npa' && (
          <g>
            <ellipse cx="162" cy="243" rx="4" ry="7" fill="#d6d9de" stroke="#777" strokeWidth="0.5" />
            <line x1="165" y1="247" x2="185" y2="252" stroke="#bbb" strokeWidth="2" strokeLinecap="round" />
          </g>
        )}
        {airway === 'igel' && (
          <g>
            {/* Bite block + supraglottic mass sits at the mouth */}
            <rect x="165" y="245" width="40" height="14" rx="3" fill="#bfe2ff" stroke="#3d7cb0" strokeWidth="0.8" />
            <rect x="165" y="245" width="40" height="14" rx="3" fill="none" stroke="#3d7cb0" strokeWidth="0.6" opacity="0.6" />
            {/* Connector tube trailing left toward the BVM */}
            <line x1="165" y1="252" x2="32" y2="252" stroke="#bfe2ff" strokeWidth="7" strokeLinecap="round" />
            <line x1="165" y1="252" x2="32" y2="252" stroke="#3d7cb0" strokeWidth="0.7" />
            {/* 15mm connector cap (off the head end, where the BVM would attach) */}
            <rect x="22" y="244" width="14" height="16" rx="1.5" fill="#cfe9ff" stroke="#3d7cb0" strokeWidth="0.8" />
          </g>
        )}
        {airway === 'lma' && (
          <g>
            <rect x="165" y="245" width="40" height="14" rx="3" fill="#e3e6eb" stroke="#777" strokeWidth="0.8" />
            <line x1="165" y1="252" x2="32" y2="252" stroke="#e3e6eb" strokeWidth="7" strokeLinecap="round" />
            <line x1="165" y1="252" x2="32" y2="252" stroke="#888" strokeWidth="0.6" />
            <rect x="22" y="244" width="14" height="16" rx="1.5" fill="#f1f3f5" stroke="#777" strokeWidth="0.8" />
          </g>
        )}
        {airway === 'ett' && (
          <g>
            {/* Tube enters mouth, trails left off the head, ends in a 15mm connector */}
            <rect x="175" y="249" width="14" height="6" fill="#1c2330" />
            <line x1="185" y1="252" x2="32" y2="252" stroke="#d6d9de" strokeWidth="4.5" strokeLinecap="round" />
            <line x1="185" y1="252" x2="32" y2="252" stroke="#777" strokeWidth="0.5" />
            <rect x="22" y="244" width="14" height="16" rx="1.5" fill="#cfd3d8" stroke="#666" strokeWidth="0.8" />
            {/* EtCO2 readout chip at bottom-left, tubing dashed from ETT connector */}
            <rect x="14" y="425" width="56" height="22" rx="3" fill="#5be1ff" opacity="0.95" stroke="#2c8da6" />
            <text x="42" y="441" textAnchor="middle" fontSize="11" fill="#0a3340" fontFamily="ui-monospace" fontWeight="700">EtCO₂</text>
            <path d="M 30 260 Q 20 380 42 425" fill="none" stroke="#5be1ff" strokeWidth="1.4" opacity="0.7" strokeDasharray="3 3" />
          </g>
        )}

        {/* ============ Defib pads (sternum / apex) — only when in shockable / cardiac arrest ============ */}
        {inArrest && (
          <g>
            {/* Sternal pad: upper-right chest (patient's right = top of image) — just below right clavicle */}
            <circle cx="320" cy="220" r="28" fill="#f6f6f6" stroke="#999" strokeWidth="1.4" opacity="0.96" />
            <circle cx="320" cy="220" r="20" fill="none" stroke="#ccc" strokeWidth="0.8" />
            <text x="320" y="226" textAnchor="middle" fontSize="18" fill="#222" fontFamily="ui-monospace" fontWeight="700">+</text>
            <line x1="320" y1="192" x2="320" y2="120" stroke="#666" strokeWidth="1.6" />
            <text x="298" y="115" fontSize="10" fill="#bbb" fontFamily="ui-monospace" fontWeight="600">STERNUM</text>
            {/* Apex pad: lower-left chest (patient's left = bottom of image) — mid-axillary line, V5-V6 level */}
            <circle cx="400" cy="295" r="28" fill="#f6f6f6" stroke="#999" strokeWidth="1.4" opacity="0.96" />
            <circle cx="400" cy="295" r="20" fill="none" stroke="#ccc" strokeWidth="0.8" />
            <text x="400" y="301" textAnchor="middle" fontSize="18" fill="#222" fontFamily="ui-monospace" fontWeight="700">−</text>
            <line x1="400" y1="323" x2="404" y2="390" stroke="#666" strokeWidth="1.6" />
            <text x="385" y="404" fontSize="10" fill="#bbb" fontFamily="ui-monospace" fontWeight="600">APEX</text>
          </g>
        )}

        {/* ============ 3-lead ECG stickers (RA upper-right chest, LA lower-left chest, LL lower-left abdomen) ============ */}
        <g opacity="0.94">
          <circle cx="260" cy="190" r="8" fill="#fff" stroke="#f44" strokeWidth="2" />
          <text x="260" y="193" textAnchor="middle" fontSize="7.5" fill="#a00" fontFamily="ui-monospace" fontWeight="700">RA</text>
          <line x1="260" y1="182" x2="230" y2="74" stroke="#f44" strokeWidth="1.4" />

          <circle cx="260" cy="315" r="8" fill="#fff" stroke="#222" strokeWidth="2" />
          <text x="260" y="318" textAnchor="middle" fontSize="7.5" fill="#222" fontFamily="ui-monospace" fontWeight="700">LA</text>
          <line x1="260" y1="323" x2="230" y2="420" stroke="#222" strokeWidth="1.4" />

          <circle cx="430" cy="310" r="8" fill="#fff" stroke="#bb0" strokeWidth="2" />
          <text x="430" y="313" textAnchor="middle" fontSize="7.5" fill="#993" fontFamily="ui-monospace" fontWeight="700">LL</text>
          <line x1="430" y1="318" x2="430" y2="420" stroke="#bb0" strokeWidth="1.4" />
        </g>

        {/* ============ SpO2 probe on patient's right index finger (top of image, x~560 y~85) ============ */}
        <g>
          <rect x="560" y="78" width="22" height="14" rx="3" fill="#1c2330" stroke="#5be1ff" strokeWidth="1.1" />
          <rect x="566" y="82" width="5" height="6" fill="#ff4d4d" />
          <path d="M 582 85 C 640 60 700 38 760 30" fill="none" stroke="#ffe066" strokeWidth="1.8" />
          <rect x="760" y="20" width="62" height="20" rx="3" fill="#1c2330" stroke="#ffe066" strokeWidth="1.2" />
          <text x="791" y="34" textAnchor="middle" fontSize="11" fill="#ffe066" fontFamily="ui-monospace" fontWeight="700">SpO₂</text>
        </g>

        {/* ============ BP cuff on patient's left upper arm (bottom of image, x~320..420 y~340..380) ============ */}
        <g>
          <rect x="280" y="340" width="120" height="40" rx="4" fill="#3a4255" stroke="#5b6478" strokeWidth="1.2" />
          <line x1="280" y1="352" x2="400" y2="352" stroke="#222" strokeWidth="0.6" opacity="0.6" />
          <line x1="280" y1="368" x2="400" y2="368" stroke="#222" strokeWidth="0.6" opacity="0.6" />
          <text x="340" y="365" textAnchor="middle" fontSize="11" fill="#a8b0c2" fontFamily="ui-monospace" fontWeight="700">NIBP</text>
          {/* Tubing to monitor (top-right) */}
          <path d="M 400 348 Q 460 320 540 80" fill="none" stroke="#3a4255" strokeWidth="2.6" />
        </g>

        {/* ============ IV cannula on patient's left dorsal hand + line to bag at top edge ============ */}
        <g>
          <rect x="500" y="412" width="34" height="14" rx="2.5" fill="#f0f0f0" stroke="#888" strokeWidth="0.9" />
          <rect x="515" y="406" width="4" height="22" fill="#5be1ff" />
          <path d="M 519 412 C 580 380 700 200 820 80" fill="none" stroke="#5be1ff" strokeWidth="1.8" opacity="0.9" />
          <rect x="810" y="62" width="48" height="22" rx="3" fill="#1c2330" stroke="#5be1ff" strokeWidth="1.3" />
          <text x="834" y="77" textAnchor="middle" fontSize="11" fill="#5be1ff" fontFamily="ui-monospace" fontWeight="700">IV</text>
        </g>

        {/* ============ CPR hands stack (lower sternum, animated) ============ */}
        {cpr_active && (
          <g className="als-cpr-hands">
            <ellipse cx="320" cy="252" rx="34" ry="24" fill="#dba98c" stroke="#7a4a30" strokeWidth="1.1" />
            <ellipse cx="320" cy="260" rx="28" ry="18" fill="#c89779" />
            <path d="M 294 240 Q 320 230 346 240" fill="none" stroke="#7a4a30" strokeWidth="1" />
            <path d="M 294 248 Q 320 238 346 248" fill="none" stroke="#7a4a30" strokeWidth="0.8" opacity="0.7" />
          </g>
        )}
      </svg>

      {/* Bottom context strip */}
      <div className="absolute bottom-2 left-3 right-3 flex justify-between text-[10px] uppercase tracking-widest text-mono-white/40 font-mono pointer-events-none">
        <span>Adult · supine · head left</span>
        <span>{current_phase.replace('_', ' ')}{cpr_active ? ` · CPR ${cpr_quality_pct}%` : ''}</span>
      </div>
    </div>
  );
}
